import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import { isEventOver } from './utils';
import {
    getWsInterface,
    WS_CONNECTION_NAME,
    UNNAMED_WS_HANDLER_SCOPE,
} from '../../spa/Utils/Websockets';
import isArray from 'lodash/isArray';
import { selectionArrayToObject } from '~shared/utils/selections-utils';

export const WS_INPLAY_UPDATE_ACTION = 'inplay_update';
export const WS_PRICE_UPDATE_ACTION = 'price_update';

const wsInterface = getWsInterface();

/**
 * Sets if the handlers have been set or not
 * @type {Boolean}
 */
let isInplayEventHandlersSet = false;

/**
 * A Set holding the list of inplay update event listeners
 * @type {Set}
 */
const inplayEventUpdateListeners = new Set();

/**
 * The inplay event update websocket handler
 * @param  {Array} args The arguments passed from the websocket
 * @return {void}
 */
const inplayEventUpdateHandler = (...args) => {
    const listeners = Array.from(inplayEventUpdateListeners);

    listeners.forEach(listener => listener(...args));
};

/**
 * A helper to set the websocket handlers
 * @param {String} [wsHandlerScope=UNNAMED_WS_HANDLER_SCOPE] a unique scope for the handler e.g. "RacingWidget-horse-racing"
 * @return {void}
 */
const setInplayEventHandlers = (wsHandlerScope = UNNAMED_WS_HANDLER_SCOPE) => {
    wsInterface.requestHandler(WS_CONNECTION_NAME).registerHandler(WS_INPLAY_UPDATE_ACTION, inplayEventUpdateHandler, wsHandlerScope);
    wsInterface.requestHandler(WS_CONNECTION_NAME).registerHandler(WS_PRICE_UPDATE_ACTION, inplayEventUpdateHandler, wsHandlerScope);

    isInplayEventHandlersSet = true;
};

/**
 * Format to subscription/unsubscription structure
 *
 * @param {Array} events structure: [{event_id: id, markets: [mId,...]}, ...]
 * @return {Array} structure [{event_id: id, market_id: mId }, ...]
 * (one element per eventId, marketId combination)
 */
const formatToSubscribe = events => events.reduce((eventsAcc, currEvent) => {
    const { event_id: eventId, markets } = currEvent;
    const elementsToAdd = markets.length ?
        markets.map(marketId => ({
            event_id: eventId,
            market_id: marketId,
        })) : [{
            event_id: eventId,
        }];

    return [
        ...eventsAcc,
        ...elementsToAdd,
    ];
}, []);

/**
 * Starts listening for inplay event updates
 * @param  {Array} events An array of events and market objects [{event_id: id, markets: [id]}]
 * @param {String} [wsHandlerScope=UNNAMED_WS_HANDLER_SCOPE] a unique scope for the handler e.g. "RacingWidget-horse-racing"
 * @return {void}
 */
export function startListeningForInplayEventUpdates(events, wsHandlerScope = UNNAMED_WS_HANDLER_SCOPE) {
    const eventsToSubscribe = formatToSubscribe(events);
    wsInterface.requestHandler(WS_CONNECTION_NAME).subscribe(eventsToSubscribe);

    if (!isInplayEventHandlersSet) {
        wsInterface.onCloseConnections(() => {
            isInplayEventHandlersSet = false;
        });

        setInplayEventHandlers(wsHandlerScope);
    }
}

/**
 * Stop listening for inplay event updates
 * @param  {Array} events An array of events and market objects [{event_id: id, markets: [id]}]
 * @deprecated since AM-2224
 * @return {void}
 */
export function stopListeningForInplayEventUpdates(events) {
    wsInterface.requestHandler(WS_CONNECTION_NAME).unsubscribe(events);
}

/**
 * Subscribes to event updates
 * @param  {Function} callback A callback that is called with the event data
 * @return {Function} A callback to unsubscribe from the event
 */
export function onInplayEventUpdate(callback) {
    inplayEventUpdateListeners.add(callback);

    return function unsubscribe() {
        inplayEventUpdateListeners.delete(callback);
    };
}

/**
 * Handles inplay selection updates
 * @param {Object} selectionData The selection data
 * @param {Function} callback The function to call back with the selection update
 * @return {Function} A callback to unsubscribe from the selection update
 */
export function onInplaySelectionUpdate({ selectionId, marketId, eventId }, callback) {

    return onInplayEventUpdate(({ event = {} } = {}) => {
        if (String(event.id) === String(eventId)) {
            const eventSuspended = event.suspended;
            const markets = event.markets || [];
            const market = markets.find(({ id }) => String(id) === String(marketId));

            if (isUndefined(market) || isEmpty(market.selections)) {
                callback({ suspended: eventSuspended });
            } else {
                const selections = market.selections;
                const marketSuspended = market.suspended;

                if (!isUndefined(selections[selectionId])) {
                    const suspended = eventSuspended || marketSuspended || selections[selectionId].suspended;
                    callback({ ...selections[selectionId], id: selectionId, suspended });
                }
            }
        }
    });
}

/**
 * Handle on inplay event over by event id
 * @param  {String|Number} id The event id to listen for
 * @param  {Function} callback A callback that is called when the event is over
 * @return {Function} A callback to unsubscribe from the event
 */
export function onInplayEventOverById(id, callback) {
    return onInplayEventUpdate(({ event = {} } = {}) => {
        if (String(event.id) === String(id) && isEventOver(event.match_status)) {
            callback({event});
        }
    });
}

/**
 * Support function to convert a message on the main ws channel to the "old" inplay format. Ideally you
 * do not need this. It was written to support the inplay event detail page.
 *
 * @todo AM-2540 That page uses a store that converts this legacy inplay format into a format that
 * is close enough to the new main socket format, and should be refactored, deprecating this function. This is not
 * an elegant solution but minimises regression for QA on what was already a pretty large story.
 *
 * Sample from old inplay_update -> new price_update message
 * NEW
 * {
 *      "active": true,
 *      "id": 834,
 *      "market_suspended": null,
 *      "markets": [],
 *      "suspended": false,
 *      "tradable": true
 * }
 *
 * OLD
 * {
 *    "active": true,
 *    "id": 834,
 *    "is_inplay": true, //
 *    "markets": [],
 *    "meta": false,
 *    "suspended": false,
 *    "timestamp": "2021-01-20 11:33:59.271946",
 *    "tradable": true
 * }
 *
 * @param {Object} event new format event
 * @param {Object} wsMessage the original websocket message
 * @returns {Object} the old format event
 */
export function convertToLegacyInplayEvent(event, wsMessage) {
    const staticFieldsToAdd = {};
    const dynamicFieldsToAdd = {};
    let markets = [];
    if (event.markets) {
        markets = event.markets.map(market => convertToLegacyInplayMarket(market, event.id, wsMessage));
    }

    return { ...event, ...{ markets }, ...staticFieldsToAdd, ...dynamicFieldsToAdd };
}

/**
 * NEW
 * {
 *      "active": true,
 *      "extra_info": null,
 *      "id": 3,
 *      "name": "3-Way",
 *      "restrict_cashout": false,
 *      "selections": {},
 *      "suspended": false,
 *      "tradable": true
 * }
 *
 * OLD
 * {
 *      "action": "change",
 *      "active": true,
 *      "feed_id": "100654047", // unused
 *      "id": 3,
 *      "name": "3-Way",
 *      "restrict_cashout": false,
 *      "selections": [],
 *      "suspended": false,
 *      "tradable": true,
 *      "use_feed_ids": true // unused
 * }
 *
 * @param {Object} market new format market
 * @param {Number} eventId the event id
 * @returns {Object} the old format market
 */
function convertToLegacyInplayMarket(market, eventId) {
    const staticFieldsToAdd = {};
    const dynamicFieldsToAdd = {};
    const selections = [];
    if (isArray(market.selections)) {
        market.selections = selectionArrayToObject(market.selections);
    }
    Object.keys(market.selections).forEach((selectionId) => {
        selections[selections.length] = convertToLegacyInplaySelection(selectionId, market.selections[selectionId], eventId, market.id);
    });

    delete market.market_suspended; // not safe to use

    return { ...market, ...staticFieldsToAdd, ...dynamicFieldsToAdd, ...{ selections } };
}

/**
 * OLD
 * {
 *      "active": true,
 *      "decimal_price": 2.1,
 *      "event_id": 834,
 *      "extra_info": {},
 *      "fraction": false,
 *      "fraction_price": "11/10",
 *      "id": 100756,
 *      "market_id": 3,
 *      "name": "Germany",
 *      "order": 1000,
 *      "price": 2.1,
 *      "selection_type_id": 1,
 *      "specialoddsvalue": "",
 *      "suspended": false,
 *      "tradable": true,
 *      "type": "1"
 * },
 *
 * NEW
 * "100757": {
 *      "active": true,
 *      "decimal_price": 3,
 *      "extra_info": {},
 *      "fraction_price": "2/1",
 *      "name": "Draw",
 *      "order": 1000,
 *      "outcome": null,
 *      "price": 3,
 *      "selection_type_id": 2,
 *      "sp_only": false,
 *      "suspended": false,
 *      "tradable": true
 *  }
 *
 * @param {String} id selection id
 * @param {Object} selection new format selection object
 * @param {Number} event_id event id
 * @param {Number} market_id market id
 * @returns {Object} the old format selection
 */
function convertToLegacyInplaySelection(id, selection, event_id, market_id) {
    const staticFieldsToAdd = {};
    const dynamicFieldsToAdd = {
        event_id,
        id: parseInt(id, 10),
        market_id,
    };

    // delete selection.sp_only; // can probably ignore this, just leave the field there...

    return { ...selection, ...staticFieldsToAdd, ...dynamicFieldsToAdd };
}
